import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Bitcoin } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import SiteNav from "@/components/SiteNav";
import ComplianceFooter from "@/components/ComplianceFooter";
import { api, formatApiError } from "@/lib/api";

const TIERS = [
  {
    key: "patron_monthly",
    name: "Patron",
    price: "$95",
    tag: "The Introduction",
    features: ["Full directory access", "Verified photography & profiles", "Direct contact details", "Discreet billing descriptor"],
  },
  {
    key: "signature_monthly",
    name: "Signature",
    price: "$295",
    tag: "The Connoisseur",
    features: ["Everything in Patron", "Saved favorites & travel calendars", "New companion previews", "Priority member support"],
    featured: true,
  },
  {
    key: "noir_monthly",
    name: "Noir Circle",
    price: "$695",
    tag: "The Inner Sanctum",
    features: ["Everything in Signature", "Concierge introduction requests", "Invitation-only Society events", "Dedicated member line"],
  },
];

export default function Membership() {
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();

  const subscribe = async (lookupKey) => {
    setBusy(lookupKey);
    try {
      let userId;
      try {
        const { data } = await api.get("/auth/me");
        userId = data.id;
      } catch {
        toast.error("Please sign in or create a client account first.");
        navigate("/join/client");
        return;
      }
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
        user_id: userId,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy("");
    }
  };

  return (
    <div className="bg-obsidian min-h-screen">
      <Seo title="Membership — Monroe Society" siteName="Monroe Society" description="Client memberships unlock the full Monroe Society directory of independent, verified companions." />
      <SiteNav />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pt-36 pb-24">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs uppercase tracking-[0.35em] text-gold/80 mb-4">Client Memberships</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-amber-100/95">
            Entry is <span className="italic gold-text">earned</span>
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-base text-neutral-400 font-light leading-relaxed">
            Membership unlocks the full directory — verified profiles, photography, and direct contact with the
            Society's companions. Companions set their own rates, arranged privately; the Society never touches them.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.key}
              data-testid={`client-tier-${t.key}`}
              className={`relative p-8 lg:p-10 border transition-all duration-500 card-glow ${
                t.featured ? "bg-elevated border-amber-500/40" : "bg-onyx hairline-gold"
              }`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0, transition: { delay: i * 0.12 } }}
            >
              {t.featured && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold text-obsidian text-[10px] uppercase tracking-[0.25em] font-medium">
                  Most Chosen
                </span>
              )}
              <p className="text-[11px] uppercase tracking-[0.3em] text-gold/70 mb-2">{t.tag}</p>
              <h2 className="font-serif text-3xl font-light text-amber-100/95">{t.name}</h2>
              <p className="mt-3 mb-8">
                <span className="font-serif text-4xl gold-text">{t.price}</span>
                <span className="text-sm text-neutral-500"> / month</span>
              </p>
              <ul className="space-y-3 mb-10">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-neutral-300 font-light">
                    <Check size={14} className="text-gold mt-0.5 shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`subscribe-${t.key}`}
                onClick={() => subscribe(t.key)}
                disabled={!!busy}
                className={`w-full py-4 text-xs uppercase tracking-[0.25em] transition-all duration-300 disabled:opacity-50 ${
                  t.featured
                    ? "bg-gold text-obsidian font-medium hover:bg-gold-bright hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]"
                    : "border hairline-gold text-amber-100/90 hover:border-amber-400/60 hover:bg-amber-500/5"
                }`}
              >
                {busy === t.key ? "Preparing Checkout…" : "Become a Member"}
              </button>
              <button
                disabled
                data-testid={`crypto-subscribe-${t.key}`}
                className="mt-3 w-full py-3.5 border border-dashed hairline-gold text-[11px] uppercase tracking-[0.15em] text-neutral-500 cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <Bitcoin size={14} className="text-gold/50" />
                Bitcoin — Coming Soon
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-center text-xs text-neutral-600 max-w-lg mx-auto leading-relaxed">
          Secure, discreetly billed checkout. All sales final per our{" "}
          <Link to="/terms" className="text-gold/70 hover:text-gold">Terms of Service</Link>. Bitcoin and additional
          currencies arrive with our production payment processor.
        </p>
      </main>
      <ComplianceFooter />
    </div>
  );
}
